import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';

type GetListingEligibility = (listing: AccommodationListingDTO) => boolean | undefined;

/**
 * Removes the listings the scenario answers `false` for.
 *
 * - `true` keeps the listing.
 * - `undefined` (not known yet) keeps the listing, so the console does not show its empty state
 *   while the answers are still arriving.
 * - `false` removes the listing.
 */
export const filterOutIneligibleListings = (
  listings: ReadonlyArray<AccommodationListingDTO>,
  getListingEligibility: GetListingEligibility,
): ReadonlyArray<AccommodationListingDTO> => {
  return listings.filter(listing => getListingEligibility(listing) !== false);
};

/** True when there is at least one listing and the scenario answers `false` for every one of them. */
export const isEveryListingIneligible = (listings: ReadonlyArray<AccommodationListingDTO>, getListingEligibility: GetListingEligibility): boolean => {
  return listings.length > 0 && listings.every(listing => getListingEligibility(listing) === false);
};

const RANK_BY_ELIGIBILITY_GROUP = {
  eligible: 0,
  unknown: 1,
  ineligible: 2,
} as const;

const getEligibilityGroupRank = (eligibility: boolean | undefined): number => {
  if (eligibility === true) {
    return RANK_BY_ELIGIBILITY_GROUP.eligible;
  }
  if (eligibility === false) {
    return RANK_BY_ELIGIBILITY_GROUP.ineligible;
  }
  return RANK_BY_ELIGIBILITY_GROUP.unknown;
};

/**
 * Groups already-sorted listings by eligibility: `true` first, then `undefined`, then `false`.
 *
 * The order inside each group is the input order, so the console's chosen sort still holds there.
 * Example: sorted [A (false), B (true), C (undefined), D (true)] becomes [B, D, C, A].
 */
export const sortListingsByEligibility = (
  sortedListings: ReadonlyArray<AccommodationListingDTO>,
  getListingEligibility: GetListingEligibility,
): ReadonlyArray<AccommodationListingDTO> => {
  const listingsWithRank = sortedListings.map((listing, index) => {
    return { listing, index, rank: getEligibilityGroupRank(getListingEligibility(listing)) };
  });
  // The index tie-break keeps the sort stable whatever the engine's sort guarantees.
  listingsWithRank.sort((first, second) => first.rank - second.rank || first.index - second.index);
  return listingsWithRank.map(listingWithRank => listingWithRank.listing);
};
